import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api, resolveImageUrl } from '@/lib/axios';
import { CartItem } from '@/types';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, CheckCircle, Trash2, Plus, Minus, ArrowLeft, ShoppingBag, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { STORE_WHATSAPP_NUMBER } from '@/lib/constants';

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const paymentStatus = searchParams.get('status');
  const orderIdParam = searchParams.get('orderId');

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [name, setName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('Retiro en Taller (Gratis)');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    const saved = localStorage.getItem('mila_cart');
    if (saved) {
      try {
        setCartItems(JSON.parse(saved));
      } catch {
        setCartItems([]);
      }
    } else {
      setCartItems([]);
    }
  };

  const total = cartItems.reduce((acc, item) => acc + (item.unitPrice * item.quantity), 0);

  const updateQuantity = (idx: number, delta: number) => {
    const updated = [...cartItems];
    const newQty = updated[idx].quantity + delta;
    if (newQty <= 0) {
      removeItem(idx);
      return;
    }
    updated[idx].quantity = newQty;
    setCartItems(updated);
    localStorage.setItem('mila_cart', JSON.stringify(updated));
    window.dispatchEvent(new Event('cart-updated'));
  };

  const removeItem = (idx: number) => {
    const updated = cartItems.filter((_, i) => i !== idx);
    setCartItems(updated);
    localStorage.setItem('mila_cart', JSON.stringify(updated));
    window.dispatchEvent(new Event('cart-updated'));
    toast.info('Producto quitado del carrito');
  };

  const handleCheckout = async (paymentMethod: 'mercadopago' | 'whatsapp') => {
    if (!name.trim() || !whatsapp.trim()) {
      toast.error('Por favor completá tu nombre y WhatsApp');
      return;
    }

    if (cartItems.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    try {
      setIsSubmitting(true);
      // 1. Crear sesión de carrito efímera
      const cartRes = await api.post<{ id: string }>('/cart');
      const cartId = cartRes.data.id;

      // 2. Checkout
      const checkoutRes = await api.post<{ orderId: number; mpCheckoutUrl?: string; total: number }>(
        `/cart/${cartId}/checkout`,
        {
          name: name.trim(),
          whatsApp: whatsapp.trim(),
          email: email.trim() || null,
          address: address.trim() || null,
          deliveryMethod,
          paymentMethod,
          total,
          notes: notes.trim() || null,
          items: cartItems.map(item => ({
            productId: item.productId,
            productName: item.productName,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            color: item.color,
            variantName: item.variantName,
            notes: item.notes || (item.base ? `Base: ${item.base}` : '')
          }))
        }
      );

      const orderId = checkoutRes.data.orderId;
      localStorage.removeItem('mila_cart');
      setCartItems([]);
      window.dispatchEvent(new Event('cart-updated'));

      if (paymentMethod === 'mercadopago' && checkoutRes.data.mpCheckoutUrl) {
        toast.success('Redirigiendo a Mercado Pago...');
        window.location.href = checkoutRes.data.mpCheckoutUrl;
      } else {
        // Redirigir a WhatsApp con el mensaje estructurado con todas las variables elegidas
        const itemsDetail = cartItems.map((i) => {
          const vars = [];
          if (i.variantName) vars.push(`*Variante:* ${i.variantName}`);
          if (i.color) vars.push(`*Color:* ${i.color}`);
          if (i.base) vars.push(`*Base:* ${i.base}`);
          const varsStr = vars.length > 0 ? `\n   ↳ ${vars.join(' | ')}` : '';
          return `• *${i.productName}* x${i.quantity} — $${(i.unitPrice * i.quantity).toLocaleString('es-AR')}${varsStr}`;
        }).join('\n');

        const message = `¡Hola Mila Design! 👋 Quiero confirmar mi pedido *#${orderId}*.\n\n` +
          `👤 *Cliente:* ${name.trim()}\n` +
          `📱 *WhatsApp:* ${whatsapp.trim()}\n` +
          (email ? `✉️ *Email:* ${email.trim()}\n` : '') +
          `📦 *Entrega:* ${deliveryMethod}\n` +
          (address ? `📍 *Dirección:* ${address.trim()}\n` : '') +
          `\n🛍️ *Piezas y Variables Seleccionadas:*\n${itemsDetail}\n\n` +
          `💰 *Total:* $${total.toLocaleString('es-AR')}\n` +
          (notes ? `📝 *Nota:* ${notes.trim()}\n\n` : '\n') +
          `¿Me confirman para coordinar el pago y tiempo de impresión? ¡Muchas gracias!`;

        const waUrl = `https://wa.me/${STORE_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
        window.open(waUrl, '_blank');
        toast.success(`¡Pedido #${orderId} registrado con éxito en el taller!`);
      }
    } catch {
      toast.error('Ocurrió un error al procesar el pedido. Por favor intentá nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (paymentStatus === 'success') {
    return (
      <div className="container mx-auto py-24 px-4 max-w-xl text-center space-y-4">
        <CheckCircle className="h-16 w-16 text-primary mx-auto" />
        <h1 className="text-3xl font-bold">¡Pago Aprobado!</h1>
        <p className="text-muted-foreground">
          Tu pedido {orderIdParam ? `#${orderIdParam}` : ''} ya ingresó al taller y comenzamos a imprimirlo según los tiempos de producción.
        </p>
        <Button asChild className="mt-4">
          <Link to="/">Volver al Inicio</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10 md:py-16 px-4 max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">Finalizar Pedido</h1>
          <p className="text-sm text-muted-foreground mt-1">Revisá las variantes seleccionadas y completá tus datos de entrega.</p>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/tienda" className="flex items-center gap-1.5 text-xs">
            <ArrowLeft className="h-4 w-4" /> Seguir Comprando
          </Link>
        </Button>
      </div>
      
      <div className="grid md:grid-cols-2 gap-10 lg:gap-14">
        {/* Formulario de Datos */}
        <div className="space-y-8">
          <section className="space-y-4 bg-card p-6 rounded-2xl border border-border/70 shadow-2xs">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">1</span>
              Tus Datos de Contacto
            </h2>
            <div className="grid gap-3.5">
              <div className="space-y-1.5">
                <Label htmlFor="name">Nombre y Apellido *</Label>
                <Input
                  id="name"
                  required
                  placeholder="Ej. Paula Gómez"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="whatsapp">WhatsApp (para avisarte cuando esté listo) *</Label>
                <Input
                  id="whatsapp"
                  required
                  placeholder="Ej. 11 5555-4444"
                  value={whatsapp}
                  onChange={e => setWhatsapp(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email (opcional)</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="space-y-4 bg-card p-6 rounded-2xl border border-border/70 shadow-2xs">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">2</span>
              Método de Entrega
            </h2>
            <div className="grid gap-3">
              {[
                { title: 'Retiro en Taller (Gratis)', desc: 'Coordinamos el día de entrega por WhatsApp.' },
                { title: 'Envío por Correo / Paquetería', desc: 'A todo el país. Cotizamos el costo exacto según tu CP.' },
                { title: 'Punto de Encuentro', desc: 'Zona acordada en CABA / GBA.' }
              ].map(d => (
                <label
                  key={d.title}
                  className={`flex items-start gap-3 p-3.5 border rounded-xl cursor-pointer transition-all ${
                    deliveryMethod === d.title ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'hover:bg-muted/40 border-border/70'
                  }`}
                >
                  <input
                    type="radio"
                    name="delivery"
                    className="mt-1 text-primary focus:ring-primary"
                    checked={deliveryMethod === d.title}
                    onChange={() => setDeliveryMethod(d.title)}
                  />
                  <div>
                    <div className="font-medium text-sm text-foreground">{d.title}</div>
                    <div className="text-xs text-muted-foreground">{d.desc}</div>
                  </div>
                </label>
              ))}

              {deliveryMethod.includes('Envío') && (
                <div className="space-y-1.5 pt-2">
                  <Label htmlFor="address">Dirección de Envío y Código Postal *</Label>
                  <Input
                    id="address"
                    placeholder="Ej. Av. Santa Fe 1234, Piso 4 B, CABA (CP 1425)"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                  />
                </div>
              )}
            </div>
          </section>

          <section className="space-y-2 bg-card p-6 rounded-2xl border border-border/70 shadow-2xs">
            <Label htmlFor="notes" className="text-sm font-semibold">Notas o indicaciones especiales para el taller</Label>
            <Input
              id="notes"
              placeholder="Ej. Es para un regalo de cumpleaños el próximo viernes..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </section>
        </div>

        {/* Resumen del Carrito con Variables */}
        <div>
          <Card className="sticky top-24 bg-card shadow-sm border-border/80 rounded-2xl overflow-hidden">
            <CardHeader className="pb-4 bg-muted/20 border-b border-border/60">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5 text-primary" /> Resumen de Compra
                </CardTitle>
                <Badge variant="secondary" className="font-semibold text-xs">
                  {cartItems.reduce((acc, i) => acc + i.quantity, 0)} {cartItems.reduce((acc, i) => acc + i.quantity, 0) === 1 ? 'pieza' : 'piezas'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-5">
              {cartItems.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm space-y-3">
                  <ShoppingBag className="h-10 w-10 mx-auto text-muted-foreground/40" />
                  <p>El carrito está vacío actualmente.</p>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/tienda">Explorar Catálogo 3D</Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 max-h-[380px] overflow-y-auto pr-1 divide-y divide-border/60">
                  {cartItems.map((item, idx) => {
                    const resolvedImg = resolveImageUrl(item.imageUrl);
                    return (
                      <div key={idx} className="pt-3 first:pt-0 space-y-2.5">
                        <div className="flex items-start gap-3">
                          {/* Miniatura del producto / variante */}
                          <div className="w-16 h-16 bg-muted/60 border border-border/60 rounded-xl overflow-hidden shrink-0 flex items-center justify-center shadow-2xs">
                            {resolvedImg ? (
                              <img src={resolvedImg} alt={item.productName} className="w-full h-full object-cover" />
                            ) : (
                              <Layers className="h-6 w-6 text-muted-foreground/40" />
                            )}
                          </div>

                          {/* Info y Variables Seleccionadas */}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm text-foreground truncate">{item.productName}</h4>
                            <div className="text-xs font-semibold text-primary mt-0.5">
                              ${item.unitPrice.toLocaleString('es-AR')} c/u
                            </div>

                            {/* Detalle visual de variables elegidas */}
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                              {item.variantName && (
                                <Badge variant="secondary" className="text-[11px] px-2 py-0.5 font-medium bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20">
                                  Var: {item.variantName}
                                </Badge>
                              )}
                              {item.color && (
                                <Badge variant="outline" className="text-[11px] px-2 py-0.5 font-medium border-border/80 flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full bg-primary/70 shrink-0" />
                                  Color: {item.color}
                                </Badge>
                              )}
                              {item.base && (
                                <Badge variant="outline" className="text-[11px] px-2 py-0.5 font-medium border-border/80">
                                  Base: {item.base}
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Subtotal por Item */}
                          <div className="text-right shrink-0">
                            <div className="font-bold text-sm text-foreground">
                              ${(item.unitPrice * item.quantity).toLocaleString('es-AR')}
                            </div>
                          </div>
                        </div>

                        {/* Fila de Controles de Cantidad y Quitar */}
                        <div className="flex items-center justify-between pt-1">
                          <div className="flex items-center border border-border/80 rounded-lg bg-muted/30 p-0.5">
                            <button
                              type="button"
                              onClick={() => updateQuantity(idx, -1)}
                              className="w-7 h-7 flex items-center justify-center hover:bg-background rounded-md transition-colors text-muted-foreground hover:text-foreground"
                              title="Restar unidad"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-8 text-center text-xs font-bold">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => updateQuantity(idx, 1)}
                              className="w-7 h-7 flex items-center justify-center hover:bg-background rounded-md transition-colors text-muted-foreground hover:text-foreground"
                              title="Sumar unidad"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className="text-xs text-destructive/80 hover:text-destructive hover:underline inline-flex items-center gap-1 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Quitar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Subtotal y Entrega */}
              <div className="space-y-2 text-sm pt-4 border-t border-border/70">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold text-foreground">${total.toLocaleString('es-AR')}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Entrega</span>
                  <span>{deliveryMethod.includes('Gratis') ? 'Gratis' : 'A convenir con el taller'}</span>
                </div>
              </div>

              {/* Total final */}
              <div className="flex justify-between items-center pt-3 border-t border-border text-xl font-bold">
                <span className="text-foreground">Total</span>
                <span className="text-primary text-2xl font-extrabold">${total.toLocaleString('es-AR')}</span>
              </div>

              {/* Botones de Pago / Confirmación */}
              <div className="space-y-3 pt-2">
                <Button
                  className="w-full text-base gap-2 bg-[#25D366] hover:bg-[#20ba5a] text-white shadow-sm font-semibold h-12 rounded-xl transition-transform active:scale-[0.99]"
                  size="lg"
                  disabled={isSubmitting || cartItems.length === 0}
                  onClick={() => handleCheckout('whatsapp')}
                >
                  <MessageCircle className="h-5 w-5" />
                  Confirmar y Pedir por WhatsApp
                </Button>

                <Button
                  variant="outline"
                  className="w-full text-sm gap-2 bg-muted/40 text-muted-foreground hover:bg-muted/40 cursor-not-allowed border-dashed border-border/80 h-11 rounded-xl"
                  size="lg"
                  disabled
                  title="Mercado Pago estará disponible próximamente"
                >
                  Pagar con Mercado Pago <span className="text-[11px] bg-muted px-2 py-0.5 rounded font-medium border border-border/60">(Próximamente)</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
